/**
 * The score document.
 *
 * This is the editor's own model, not MusicXML. MusicXML is read and written at the edges
 * of the package and never held in memory: it keeps a separate timeline per part, moves
 * around inside a measure with `backup` and `forward`, and lets its `divisions` change
 * partway through a file. Those are reasonable choices for an interchange format and ruin
 * an editor, where the questions are "what is at this beat" and "what did that edit
 * change".
 *
 * Three decisions shape everything below.
 *
 * **The timeline is global.** Time signatures, key signatures, tempo, rehearsal marks,
 * repeats and barlines belong to {@link Score.measures}, one entry per bar of the piece,
 * not to each part separately. MusicXML repeats them in every part and every editor built
 * on that shape spends the rest of its life keeping the copies in step. Parts hold notes
 * and nothing else.
 *
 * **Content is addressed by id, never by position.** A part stores its bars in
 * {@link Part.measures}, keyed by the id of the global measure they fill. Inserting a bar
 * in the middle of the piece is then one entry added to {@link Score.measures}; no part
 * has to be renumbered and no index anywhere goes stale.
 *
 * **Everything is readonly.** An edit produces a new document rather than mutating this
 * one, which is what makes undo a matter of keeping the old value and makes it cheap for
 * the renderer to know what actually changed.
 */

import type {
  DirectionId,
  EventId,
  GroupId,
  MeasureId,
  PartId,
  ScoreId,
  SpannerId,
  StaffId,
  VoiceId,
} from "./ids";
import type { Duration } from "./duration";
import type { NotePitch, Transposition } from "./pitch";
import type { StringTuning } from "./tab";
import type { PercussionKit } from "./percussion";
import type { Layout } from "./layout";
import type { FiguredBass } from "./figures";
import type { Harmony } from "./harmony";

/* ------------------------------------------------------------------ notation values */

/** A clef, as its shape and the staff line its shape sits on. */
export interface Clef {
  readonly sign: "G" | "F" | "C" | "percussion" | "TAB";
  /** Staff line, counted from the bottom line as 1. Treble is `G` on 2, bass `F` on 4. */
  readonly line: number;
  /** Octaves the clef displaces what is written, e.g. `-1` for a tenor G clef. */
  readonly octaveChange: number;
}

export const TREBLE: Clef = { sign: "G", line: 2, octaveChange: 0 };
export const BASS: Clef = { sign: "F", line: 4, octaveChange: 0 };
export const ALTO: Clef = { sign: "C", line: 3, octaveChange: 0 };
export const TENOR: Clef = { sign: "C", line: 4, octaveChange: 0 };

/** A key signature, as a count of sharps (positive) or flats (negative). */
export interface KeySignature {
  readonly fifths: number;
  readonly mode: "major" | "minor" | null;
}

export const C_MAJOR: KeySignature = { fifths: 0, mode: "major" };

/** A metre. `symbol` chooses how it is drawn, not what it means. */
export interface TimeSignature {
  readonly beats: number;
  readonly beatType: number;
  readonly symbol: "number" | "common" | "cut";
}

export const COMMON_TIME: TimeSignature = { beats: 4, beatType: 4, symbol: "common" };

/** A tempo mark: a note value given a speed, with optional words above it. */
export interface Tempo {
  /** Beats per minute, counted in the unit below. */
  readonly bpm: number;
  /** The note the bpm counts, e.g. `"quarter"`. */
  readonly unit: Duration;
  readonly text: string | null;
}

/** How a bar is closed off. */
export type BarlineStyle = "regular" | "double" | "final" | "dashed" | "heavy" | "none";

/** Repeat structure attached to a bar. */
export interface RepeatMarks {
  readonly start: boolean;
  readonly end: boolean;
  /** Volta numbers this bar belongs to, empty when it is in no ending. */
  readonly endings: readonly number[];
  /**
   * How many times the section this bar ends is played, on the bar carrying `end`; `null`
   * when the bar states no count and the passes are read off the ending numbers and the
   * verses under the section instead. Nothing is drawn for it: it is what a MusicXML
   * `<repeat times="3">` says.
   */
  readonly times: number | null;
}

/**
 * The signs that send a player somewhere that is not the next bar.
 *
 * A repeat is written as a pair of walls around the music it repeats; a jump is written as
 * a word at one bar and a sign at another, and the two can be anywhere in the piece. So
 * these are four independent marks rather than one, and which of them a pass obeys is the
 * whole of the convention: on the way through, a coda sign and a `Fine` are read over, and
 * only after a `D.C.` or a `D.S.` has sent the player back do they mean anything.
 *
 * What a jump is printed as is not stored, because it is not a choice: a `D.S.` in a piece
 * that has a `Fine` is `D.S. al Fine` and one in a piece that has a coda is `D.S. al Coda`.
 * Storing the words as well would let a file say `al Fine` with no `Fine` in it.
 */
export interface JumpMarks {
  /** The bar carries the segno, which is what a `dalSegno` comes back to. */
  readonly segno: boolean;
  /** The bar carries the coda sign: the music a `toCoda` skips forward to. */
  readonly coda: boolean;
  /** The end of this bar goes to the coda, on the pass after a jump and not before. */
  readonly toCoda: boolean;
  /** The piece ends at the end of this bar, on the pass after a jump and not before. */
  readonly fine: boolean;
  /** Where the end of this bar sends the player: back to the top, or back to the segno. */
  readonly jump: "daCapo" | "dalSegno" | null;
}

/* ---------------------------------------------------------------------- instruments */

/**
 * Where a part's printed key signature comes from.
 *
 * `derived` is the piece's key moved by the part's own transposition, which is what almost
 * every part of almost every score prints. `none` is the part written with no signature at
 * all, every altered note carrying its own accidental: the horns and trumpets of a
 * nineteenth-century score are written that way, and so are timpani. `sharps` and `flats`
 * choose between the two spellings of a signature that has two -- a score in G flat whose
 * harp is written in F sharp, which is the same key and a far easier page to read, the
 * pedals being named by letter.
 */
export type KeyPrinting = "derived" | "none" | "sharps" | "flats";

/** What a part is played on. */
export interface Instrument {
  readonly name: string;
  /** MusicXML sound id, e.g. `"brass.french-horn"`; `null` when unclassified. */
  readonly sound: string | null;
  /** General MIDI program, 0-based; `null` when unclassified. */
  readonly midiProgram: number | null;
  readonly transposition: Transposition;
  /**
   * The drums this part is written with, or `null` for an instrument that has pitches.
   *
   * A kit is what makes a staff a percussion staff: its lines stop being pitches and
   * become instruments. It sits on the part rather than in a table of presets because a
   * file has to say what its own lines mean -- another program's kit is not this one's.
   */
  readonly percussion: PercussionKit | null;
  /**
   * The open strings this part is played on, or `null` for an instrument with no frets.
   *
   * A tuning is what makes a staff a tab staff: its lines stop being pitches and become
   * strings. It sits on the part because an instrument is retuned piece by piece -- a
   * dropped D is a tuning, not another instrument -- and because a file has to say what its
   * own lines are before anybody can read a fret off them.
   */
  readonly tuning: readonly StringTuning[] | null;
  /**
   * How this part arrives at the signature it prints.
   *
   * Almost every part prints the piece's key moved by its own transposition, which is
   * `derived` and needs saying about none of them. The other three are conventions of the
   * instrument or of the copyist rather than facts about the music, which is why they sit
   * here and not on the bar the key is stated in: the piece is still in the key it is in,
   * and every other part still prints it that way.
   */
  readonly keyPrinting: KeyPrinting;
}

/**
 * The instrument a player picks up partway through a piece.
 *
 * A doubling is one part and one staff: the flute that takes the piccolo up for sixteen
 * bars is still the second flute's line, read by the same player off the same stand, so it
 * is written into the bar it happens in rather than into a second part that would be silent
 * for the rest of the piece.
 *
 * What it carries is what the swap actually changes -- the name printed over the change,
 * the sound, and what the player is now reading against. What it deliberately does not
 * carry is on {@link Instrument} and stays there: a kit, a tuning and the way the part
 * spells its signature belong to the staff and to the copyist rather than to the moment,
 * and a staff of pitches does not become a drum kit halfway down the page.
 */
export interface InstrumentChange {
  readonly name: string;
  /** MusicXML sound id, e.g. `"wind.flutes.piccolo"`; `null` when unclassified. */
  readonly sound: string | null;
  /** General MIDI program, 0-based; `null` when unclassified. */
  readonly midiProgram: number | null;
  readonly transposition: Transposition;
}

/* --------------------------------------------------------------------------- staves */

/** One staff of a part. A piano part has two; most parts have one. */
export interface Staff {
  readonly id: StaffId;
  /** Clef at the start of the piece. Later changes live on the bar they happen in. */
  readonly clef: Clef;
  readonly lines: number;
}

/**
 * A bracket or brace drawn down the left of the system across several parts.
 *
 * Flat by design in v1: groups do not nest, so a bracketed wood-wind section containing a
 * braced pair of flutes is written as two groups rather than one inside the other.
 */
export interface StaffGroup {
  readonly id: GroupId;
  readonly symbol: "bracket" | "brace" | "line" | "none";
  /** Whether barlines are drawn through the gap between the group's staves. */
  readonly barlineSpan: boolean;
  readonly name: string | null;
  readonly parts: readonly PartId[];
}

/* --------------------------------------------------------------------------- events */

/** Where a stem points, or `"auto"` to let the engraver decide. */
export type StemDirection = "auto" | "up" | "down";

/** How an event joins the beam running through it. */
export type BeamState = "auto" | "none" | "begin" | "continue" | "end";

/**
 * Marks written on a note head or above it, the one written after it -- a breath mark, the
 * comma over the staff that says a player may breathe once the note is played -- and the
 * small circle over a note a string player sounds as a natural harmonic.
 */
export type Articulation =
  | "staccato"
  | "staccatissimo"
  | "accent"
  | "strong-accent"
  | "tenuto"
  | "detached-legato"
  | "breath-mark"
  | "harmonic";

/**
 * A sign asking for notes the written ones do not state.
 *
 * An ornament is notation, not performance: the document records the sign the composer
 * wrote, and what a player makes of a trill in 1720 is not the same as in 1890. Nothing
 * here expands into notes: a {@link Score} carries the sign and not one reading of it.
 * Playback makes a reading of its own in `playback/realize.ts`, which a caller can switch
 * off, leaving the written notes flat.
 */
export type Ornament =
  | "trill"
  | "turn"
  | "inverted-turn"
  | "delayed-turn"
  | "mordent"
  | "inverted-mordent"
  | "shake"
  | "schleifer";

/**
 * How long a fermata asks for, as the shape it is drawn with.
 *
 * The three shapes are a convention rather than an arithmetic: square is the longest hold
 * and angled the shortest. A fermata belongs to a rest as readily as to a note -- a held
 * silence is ordinary -- so it sits on every event rather than on notes alone.
 */
export type FermataShape = "normal" | "angled" | "square";

/**
 * How the notes of a chord are spread rather than struck together.
 *
 * `"bracket"` is the sign that says the opposite: play them together, do not spread. It is
 * one field because a chord is one or the other and never both.
 */
export type Arpeggio = "up" | "down" | "bracket";

/**
 * One syllable of sung text under a note.
 *
 * A note carries several when the piece has several verses, which is why `verse` is a
 * number rather than the list being one string: verse 2 of a hymn is written under verse 1,
 * not after it. `syllabic` says whether the syllable is a word on its own or part of one,
 * which is what puts a hyphen between it and the next; `extend` draws the line that carries
 * a held vowel across the notes that follow.
 */
export interface Lyric {
  /** 1-based, as a singer counts verses. */
  readonly verse: number;
  readonly text: string;
  readonly syllabic: "single" | "begin" | "middle" | "end";
  readonly extend: boolean;
}

/**
 * A nudge away from where the engraver put something.
 *
 * Measured in tenths -- ten to the space between two staff lines -- which is the unit
 * MusicXML measures every distance in, so the number written to a file is the number held
 * here. `x` counts to the right and `y` counts *upwards*, also as the format does, even
 * though the drawn page counts y the other way; a musician asked to move a mark up should
 * not have to type a negative number for it.
 *
 * An offset says nothing about where the element belongs -- the engraver decides that, bar
 * by bar, and moves it again on the next relayout. It says only how far from there the
 * person writing the music wants it, which is what makes it survive an edit that reflows
 * the system.
 */
export interface Offset {
  readonly x: number;
  readonly y: number;
}

interface EventBase {
  readonly id: EventId;
  readonly duration: Duration;
  /** Held beyond its written length, drawn as this shape; `null` when it is not held. */
  readonly fermata: FermataShape | null;
  /**
   * Somebody else's music, printed small so a player can find their entry.
   *
   * A cue is on the event rather than on the passage because that is where it has to be
   * read from: it is drawn small, it is not sung, and it is not played -- the player whose
   * part it is plays it. It still takes its time in the bar, which is what makes the cue
   * line up with the music it is quoting. Rests carry it too, because a cue that does not
   * fill the bar it sits in needs small rests to finish it.
   */
  readonly cue: boolean;
  /**
   * Moved by hand from where the engraver put it; `null` when it sits where it was put.
   *
   * This is the last word on a page that is otherwise laid out for you, and it is kept on
   * the event rather than in a table of adjustments beside the music so that copying,
   * transposing or extracting a part carries it along with the note it belongs to. A chord
   * is one event and moves as one: there is no offset per note head.
   */
  readonly offset: Offset | null;
  /**
   * Drawn on a different staff of the part from the one its voice is on; `null` when it
   * sits on its own.
   *
   * This is the cross-staff note a keyboard part is full of: the left hand reaches up, or
   * the right hand reaches down, and the run is written where it is played while the beam
   * stays with the voice it belongs to. It is an index into {@link Part.staves}, as
   * {@link Voice.staff} is.
   *
   * It moves only where the note is *drawn*. The voice still owns the note, still counts it
   * in its own line, and still beams it with its neighbours -- which is the whole point,
   * because a run written across both staves is one run. Nothing about the sound changes,
   * and a part with one staff has nowhere to send a note, so the field stays `null` there.
   */
  readonly staff: number | null;
}

/** One or more note heads struck together. Several pitches make a chord. */
export interface NoteEvent extends EventBase {
  readonly kind: "note";
  readonly pitches: readonly NotePitch[];
  readonly stem: StemDirection;
  readonly beam: BeamState;
  readonly articulations: readonly Articulation[];
  /** Ornament signs written above the note, in the order they are to be read. */
  readonly ornaments: readonly Ornament[];
  /** Spread the chord rather than striking it together; `null` for an ordinary chord. */
  readonly arpeggio: Arpeggio | null;
  /**
   * Beams struck through the stem, as a count: `0` for none, up to `3`.
   *
   * This is the one-note tremolo -- a repeated note written short. The two-note tremolo,
   * which alternates between two written notes, is a different sign and is not this field.
   */
  readonly tremolo: number;
  /** A grace note takes its time from the event after it. */
  readonly grace: "acciaccatura" | "appoggiatura" | null;
  /** Sung text under the note, one entry per verse. Empty for an unsung note. */
  readonly lyrics: readonly Lyric[];
}

/** A silence. */
export interface RestEvent extends EventBase {
  readonly kind: "rest";
  /** A whole-measure rest is drawn and counted differently from a rest that happens to fill one. */
  readonly wholeMeasure: boolean;
}

export type MusicEvent = NoteEvent | RestEvent;

/**
 * One line of music within a staff.
 *
 * A voice belongs to this bar, not to the staff for the length of the piece: a divided
 * passage is divided where it is divided, so neighbouring bars hold different numbers of
 * them. `editor/voices.ts` is what makes and unmakes them, and what carries a caret from
 * one bar's voices to the next's.
 */
export interface Voice {
  readonly id: VoiceId;
  /** Index into {@link Part.staves}. */
  readonly staff: number;
  /** Events in the order they are played, filling the bar from its start. */
  readonly events: readonly MusicEvent[];
  /**
   * Whether this line is an ossia: an alternative reading of the bar, drawn small on a
   * staff of its own above the staff named by {@link staff}, rather than a line played
   * beside it.
   *
   * It is a voice and not a staff because everything an editor does to a line of music --
   * selecting it, typing into it, slurring it, transposing it, clicking on it -- is done
   * by finding an event in a bar's voices, so an ossia written as one is editable exactly
   * as the music under it is. What it is not is *played*: an ossia is what the player may
   * do instead, so playback, the MIDI file and the four-voice limit on a staff all pass
   * over it.
   */
  readonly ossia: boolean;
}

/* ------------------------------------------------------- directions and spanners */

/** A dynamic mark. */
export type DynamicMark =
  | "pppp"
  | "ppp"
  | "pp"
  | "p"
  | "mp"
  | "mf"
  | "f"
  | "ff"
  | "fff"
  | "ffff"
  | "sf"
  | "sfz"
  | "fp"
  | "rf"
  | "rfz";

/**
 * How a piece of text standing by the staff is written, and so what it is taken to mean.
 *
 * Every engraver's palette names these three and draws them apart, because a reader tells
 * them apart at a glance rather than by reading them: *dolce* leaning under the staff is
 * something to feel, `pizz.` upright over it is something to do, and **Allegro** in bold is
 * how fast to do it. The document keeps the meaning rather than the font, so a score can be
 * restyled without every word in it being re-typed.
 *
 * - `expression` -- italic, under the staff by default: *dolce*, *espressivo*, *cantabile*.
 * - `technique` -- upright, over the staff: `pizz.`, `con sord.`, `a2`. The plain staff text
 *   a program that has no technique style writes.
 * - `tempo` -- bold, over the staff: **Allegro**, **rit.**, **a tempo**. Text about speed
 *   only; the speed itself is the bar's tempo, which is a different thing and plays back.
 */
export type WordsStyle = "expression" | "technique" | "tempo";

/** Something written at a point in the music, hung off the event it belongs to. */
export interface Direction {
  readonly id: DirectionId;
  readonly anchor: EventId;
  readonly placement: "above" | "below";
  readonly content:
    | { readonly kind: "dynamic"; readonly mark: DynamicMark }
    | { readonly kind: "words"; readonly text: string; readonly style: WordsStyle };
}

/**
 * Something drawn between two events, which may be in different bars.
 *
 * Held on the part rather than on a bar because it belongs to neither end of itself, and
 * named by event id so that it survives any edit that does not delete one of its ends.
 */
/**
 * The lines the document knows, all held the same way although the format writes them in
 * three different places: a slur and a glissando are notations on the two notes, a hairpin,
 * a pedal and an octave shift are directions standing beside them. Which of those it is
 * belongs to `musicxml/`, not here.
 *
 * An octave shift names what is *printed*, as the sign does: under an `8va` the notes are
 * written an octave below what they sound. The document's pitches are the ones on the staff
 * and the sign does not touch them -- it is a mark standing beside the notes, and what is
 * heard is read from the pitches themselves, as it is everywhere else.
 */
export type SpannerKind =
  | "slur"
  | "crescendo"
  | "diminuendo"
  | "glissando"
  | "pedal"
  | "8va"
  | "8vb"
  | "15ma"
  | "15mb";

/** The octave shifts, apart from the lines that are not one. */
export const OCTAVE_SHIFTS = ["8va", "8vb", "15ma", "15mb"] as const;

export type OctaveShift = (typeof OCTAVE_SHIFTS)[number];

export function isOctaveShift(kind: SpannerKind): kind is OctaveShift {
  return (OCTAVE_SHIFTS as readonly string[]).includes(kind);
}

export interface Spanner {
  readonly id: SpannerId;
  readonly kind: SpannerKind;
  readonly from: EventId;
  readonly to: EventId;
  readonly placement: "above" | "below";
}

/* ------------------------------------------------------------------------ the score */

/** One bar of one part. */
export interface PartMeasure {
  readonly voices: readonly Voice[];
  /**
   * The instrument taken up in this bar, or `null` where the part plays on.
   *
   * It is the bar that carries it, as a clef change is, because that is where the swap
   * happens and where a reader has to be told about it: everything after it is played on
   * the new instrument until another bar says otherwise, which is what
   * {@link instrumentAt} answers. The part's own {@link Instrument} is what it starts the
   * piece on and is never this.
   */
  readonly instrument: InstrumentChange | null;
  /** Clef changes taking effect in this bar, by staff index. */
  readonly clefChanges: Readonly<Record<number, Clef>>;
  readonly directions: readonly Direction[];
  /**
   * Chord symbols over this bar, each standing at a tick within it.
   *
   * They are here rather than among the directions because a chord symbol is harmony
   * rather than an instruction to the player: it is transposed with the part, it is what a
   * rhythm section reads instead of the notes, and it stands at a beat whether or not
   * anything is written there. See `harmony.ts`.
   */
  readonly harmonies: readonly Harmony[];
  /**
   * Figured bass under this bar, each stack standing at a tick within it.
   *
   * Beside the chord symbols rather than among them because the two say different things:
   * a chord symbol names a chord and is transposed with the part, a figure names an
   * interval above whatever the bass is playing and is not. See `figures.ts`.
   */
  readonly figures: readonly FiguredBass[];
}

/** One instrument's worth of the score. */
export interface Part {
  readonly id: PartId;
  readonly name: string;
  /** Short name used on systems after the first. */
  readonly abbreviation: string;
  readonly instrument: Instrument;
  readonly staves: readonly Staff[];
  /** Bars, keyed by the id of the {@link GlobalMeasure} each one fills. */
  readonly measures: Readonly<Record<MeasureId, PartMeasure>>;
  readonly spanners: readonly Spanner[];
}

/**
 * One bar of the piece, as the piece rather than as any one part sees it.
 *
 * `null` on any of the signature fields means "unchanged from the bar before"; the first
 * bar of a score therefore states all of them. {@link resolveAttributes} in `query.ts`
 * walks this list to answer what is in force at a given bar.
 */
export interface GlobalMeasure {
  readonly id: MeasureId;
  readonly time: TimeSignature | null;
  readonly key: KeySignature | null;
  readonly tempo: Tempo | null;
  readonly barline: BarlineStyle;
  readonly repeat: RepeatMarks | null;
  /** The segno, the coda and the words that jump between them; `null` in an ordinary bar. */
  readonly jump: JumpMarks | null;
  readonly rehearsal: string | null;
  /** Force a new system or page before this bar. */
  readonly breakBefore: "system" | "page" | null;
  /**
   * Ticks this bar holds, when that is not what its metre says; `null` for an ordinary bar.
   *
   * A pickup is the reason: an upbeat bar is written in the metre of the piece and holds
   * less than it, and so is the bar that closes a repeat which began mid-bar. Stating the
   * length rather than a second time signature keeps the metre one thing -- a shorter bar
   * is not a change of metre, and a reader must not print one at it.
   */
  readonly length: number | null;
}

/** Who wrote the piece and what it is called. */
export interface ScoreMeta {
  readonly title: string;
  readonly subtitle: string | null;
  readonly composer: string | null;
  readonly arranger: string | null;
  readonly lyricist: string | null;
  readonly copyright: string | null;
  readonly movementNumber: string | null;
  readonly movementTitle: string | null;
}

/** A whole piece. */
export interface Score {
  readonly id: ScoreId;
  readonly meta: ScoreMeta;
  /** The page it is printed on, and how large the music stands on it. */
  readonly layout: Layout;
  /** The global timeline: one entry per bar, in order. */
  readonly measures: readonly GlobalMeasure[];
  readonly parts: readonly Part[];
  readonly groups: readonly StaffGroup[];
}
